/// <amd-module name="Build/Admin/Pool/Components/TopPipelinesCard" />
import { ErrorComponent } from "Analytics/Shared/Analytics-Visuals-Common/ErrorComponent/ErrorComponent";
import { LoadingComponent } from "Analytics/Shared/Analytics-Visuals-Common/LoadingComponent/LoadingComponent";
import * as Resources from "Build/Admin/Pool/Resources";
import { IPipelinesConsumptionData } from "Build/Admin/Pool/Types";
import * as React from "react";
import { Chart } from "VSS/Features/Charts/Chart";
import * as Charts_Contracts from "VSS/Features/Charts/Contracts";
import { ChartProps, DataPoint, TooltipLineItem } from "VSS/Features/Charts/Contracts";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { SpinnerSize } from "VSSUI/Spinner";
import { ITopPipelinesCardState, TopPipelinesStore } from "../Stores/TopPipelinesStore";

export interface ITopPipelinesCardProps extends IVssComponentProperties {
    errorText?: string;
    isHosted: boolean;
    isPublic?: boolean;
    onDataRendered?: () => void;
    store: TopPipelinesStore;
}

export class TopPipelinesCard extends VssComponent<ITopPipelinesCardProps, ITopPipelinesCardState> {
    private _onDataRenderedCalled = false;

    constructor(props: ITopPipelinesCardProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText
        } as Readonly<ITopPipelinesCardState>;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getConsumptionState());
            this.performOnDataRendered();
        }
    }

    public componentDidUpdate(prevProps: ITopPipelinesCardProps, prevState: ITopPipelinesCardState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.performOnDataRendered();
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
        super.componentWillUnmount();
    }

    public render() {
        return (
            <Card
                className={this.props.className}
                titleProps={{
                    text: this._getChartTitle()
                }}
            >
                {this.getElementToDisplay()}
            </Card>
        );
    }

    private getElementToDisplay(): JSX.Element {
        if (this.state.errorText) {
            return <ErrorComponent className="flex-grow" errorMessage={this.state.errorText} />;
        } else if (this.state.data != null) {
            const chartData = this.state.data;
            return (
                <div className="flex-row flex-wrap flex-grow">
                    <div className="flex-grow">{this._verifyChartData(chartData) && <Chart {...this._getChartOptions(chartData)} />}</div>
                </div>
            );
        } else {
            return <LoadingComponent className="card-spinner flex-column flex-grow" size={SpinnerSize.large} />;
        }
    }

    private _getChartOptions = (chartData: IPipelinesConsumptionData[]): ChartProps => {
        const xAxisLabelValues = chartData[0].consumptionTimeline.map(node => node.date);

        const series: Charts_Contracts.DataSeries[] = [];
        chartData.forEach(pipeline => {
            series.push({ name: pipeline.name, data: pipeline.consumptionTimeline.map(node => node.consumption) });
        });

        const chartProps: ChartProps = {
            chartType: Charts_Contracts.ChartTypesConstants.StackedColumn,
            hostOptions: { height: 200 } as Charts_Contracts.ChartHostOptions,
            legend: { rightAlign: true },
            pageContext: this.context.pageContext,
            series: series,
            suppressAnimation: true,
            title: this._getChartTitle(),
            tooltip: {
                customTooltipMapping: (points: DataPoint[]): TooltipLineItem[] => {
                    return points.map(point => {
                        return {
                            styleType: {
                                color: point.color,
                                type: Charts_Contracts.TooltipLineItemMarkerType.Square
                            },
                            text: `${point.seriesName}: ${point.values[0]}%`
                        } as Charts_Contracts.TooltipLineItem;
                    });
                }
            },
            xAxis: {
                labelFormatMode: Charts_Contracts.LabelFormatModes.DateTime_DayInMonth,
                labelsEnabled: true,
                labelValues: xAxisLabelValues
            },
            yAxis: { min: 0, max: 100, title: Resources.TopFivePipelinesYAxisLabel.toLocaleUpperCase() }
        };

        return chartProps;
    };

    private _verifyChartData = (chartData: IPipelinesConsumptionData[]): boolean => {
        return chartData && chartData.length > 0 ? true : false;
    };

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getConsumptionState());
    };

    private _getChartTitle = () => {
        const { isHosted, isPublic } = this.props;
        return !isHosted
            ? isPublic
                ? Resources.TopFivePipelinesOnlineAgents
                : Resources.TopFivePipelinesPrivate
            : isPublic
            ? Resources.TopFivePipelinesPublic
            : Resources.TopFivePipelinesPrivate;
    };

    private performOnDataRendered() {
        if (this.state.data || this.state.errorText) {
            if (this.props.onDataRendered && !this._onDataRenderedCalled) {
                this.props.onDataRendered();
                this._onDataRenderedCalled = true;
            }
        }
    }
}
